import React, { Component, Fragment } from 'react';
import { isEmpty } from "lodash";
import {
    Grid,
    FormControl,
    Select,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Button,
    TextField,
    FormHelperText,
    Typography,
    Box,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DeleteOutline } from '@mui/icons-material';

import FileUpload from '../../common/FileUpload';
import DatePicker from '../../common/DatePicker';
import Dialog from '../../common/Dialog';

class CreditForm extends Component {
    constructor(props) {
        super(props);
        // Initialiser credit_user_id depuis les props ou l'URL si disponible
        const creditsForUserId = props.credits_for_user_id || 
            (typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('credits_for_user_id') : null);
        
        // Initialiser spending_demand_id depuis les props ou l'URL si disponible
        const spendingDemandId = props.spending_demand_id || 
            (typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('spending_demand_id') : null) ||
            props.credit_demand_id || "";
        
        this.state = { 
            credit_user_id: creditsForUserId ? String(creditsForUserId) : "",
            credit_ref: "",
            credit_id: "",
            credit_payment_mode: "",
            credit_bank: "",
            credit_amount: "",
            credit_files: null,
            credit_creation_date: new Date(),
            credit_spending_demand_id: spendingDemandId ? String(spendingDemandId) : "",
            
            errors: {
                credit_creation_date: "",
                credit_user_id: "",
                credit_ref: "",
                credit_payment_mode: "",
                credit_bank: "",
                credit_amount: "",
                credit_files: "",
                credit_spending_demand_id: "",
                subCredits: ""
            },
            creditList: [],
            available: "",
            subCredits: [],
            subCreditsErrors: []
        };
    }

    getSpendingDemandOptions = () => {
        const { SPENDING_DEMANDS } = this.props;

        if (!SPENDING_DEMANDS || isEmpty(SPENDING_DEMANDS)) {
            return [];
        }

        const sortedDemands = Object.values(SPENDING_DEMANDS).sort((a, b) => {
            const dateA = new Date(a.spending_demand_date || 0).getTime();
            const dateB = new Date(b.spending_demand_date || 0).getTime();
            return dateB - dateA;
        });
        return sortedDemands;
    }

    getSpendingDemandDisplay = (demand) => {
        if (!demand) return "";
        const date = demand.spending_demand_date
            ? new Date(demand.spending_demand_date).toLocaleDateString("fr-FR")
            : "";
        const title = demand.spending_demand_name || demand.spending_demand_desc || demand.spending_demand_reference || demand.spending_demand_id;
        const amount = demand.spending_demand_amount !== undefined
            ? `${Number(demand.spending_demand_amount).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
            : "";
        return [date, title, amount].filter(Boolean).join(" - ");
    }

    getSpendingDemandLabelById = (spendingDemandId) => {
        if (!spendingDemandId) return "";
        const { SPENDING_DEMANDS } = this.props;
        if (!SPENDING_DEMANDS) return spendingDemandId;
        const demand = SPENDING_DEMANDS[spendingDemandId];
        return demand ? this.getSpendingDemandDisplay(demand) : spendingDemandId;
    }

    getSpendingDemandOptionById = (spendingDemandId, options = null) => {
        if (!spendingDemandId) return null;
        const list = options || this.getSpendingDemandOptions();
        return list.find(option => option.spending_demand_id === spendingDemandId) || null;
    }

    filterSpendingDemandOptions = (options, { inputValue }) => {
        const terms = inputValue
            ? inputValue.trim().toLowerCase().split(/\s+/).filter(Boolean)
            : [];

        if (!terms.length) {
            return options;
        }

        return options.filter(option => {
            const label = this.getSpendingDemandDisplay(option).toLowerCase();
            return terms.every(term => label.includes(term));
        });
    }

    componentDidMount = () => {
        // Si un utilisateur est défini et que c'est un débit, charger les crédits de l'utilisateur
        if (this.state.credit_user_id && this.props.debit) {
            console.log('================== CreditForm componentDidMount ===================');
            console.log('credit_user_id from state:', this.state.credit_user_id);
            console.log('Loading credits for user...');
            console.log('====================================');
            this.getUserCredits(this.state.credit_user_id);
        }

        if (!this.props.debit && isEmpty(this.props.SPENDING_DEMANDS) && this.props.fetchAdminSpendingsDemand) {
            this.props.fetchAdminSpendingsDemand();
        }

        // Si spending_demand_id est présent dans l'URL avec plusieurs IDs, créer des sous-crédits
        if (!this.props.debit && this.state.credit_spending_demand_id) {
            this.initializeSubCreditsFromSpendingDemands();
        }
    }

    componentDidUpdate = (prevProps) => {
        // Si les SPENDING_DEMANDS viennent d'être chargés et qu'on attendait de créer des sous-crédits
        if (!this.props.debit && 
            isEmpty(prevProps.SPENDING_DEMANDS) && 
            !isEmpty(this.props.SPENDING_DEMANDS) && 
            this.state.credit_spending_demand_id &&
            this.state.subCredits.length === 0) {
            this.initializeSubCreditsFromSpendingDemands();
        }
    }

    initializeSubCreditsFromSpendingDemands = () => {
        const spendingDemandIds = this.state.credit_spending_demand_id;
        if (!spendingDemandIds) return;

        // Séparer les IDs par virgule et nettoyer les espaces
        const ids = spendingDemandIds.split(',').map(id => id.trim()).filter(Boolean);
        
        if (ids.length === 0) return;

        // Vérifier si les SPENDING_DEMANDS sont disponibles
        const { SPENDING_DEMANDS } = this.props;
        if (isEmpty(SPENDING_DEMANDS)) {
            // Si pas encore chargés, on attendra componentDidUpdate
            return;
        }

        // Créer un sous-crédit pour chaque ID
        const newSubCredits = ids.map(spendingDemandId => {
            const demand = SPENDING_DEMANDS[spendingDemandId];
            return {
                s_credit_amount: demand && demand.spending_demand_amount 
                    ? String(demand.spending_demand_amount) 
                    : "",
                s_credit_desc: demand && demand.spending_demand_name 
                    ? demand.spending_demand_name 
                    : (demand && demand.spending_demand_desc ? demand.spending_demand_desc : ""),
                s_credit_spending_demand: spendingDemandId
            };
        });

        // Initialiser les erreurs pour chaque sous-crédit
        const newSubCreditsErrors = newSubCredits.map(() => ({}));

        this.setState({
            subCredits: newSubCredits,
            subCreditsErrors: newSubCreditsErrors,
            errors: {
                ...this.state.errors,
                subCredits: ""
            }
        });

        // Calculer le total des montants et mettre à jour credit_amount si tous les montants sont disponibles
        if (newSubCredits.length > 0) {
            const total = newSubCredits.reduce((sum, subCredit) => {
                const amount = parseFloat(subCredit.s_credit_amount);
                return sum + (Number.isNaN(amount) ? 0 : amount);
            }, 0);
            
            // Mettre à jour le montant total si tous les sous-crédits ont un montant valide
            if (total > 0) {
                const allAmountsValid = newSubCredits.every(subCredit => {
                    const amount = parseFloat(subCredit.s_credit_amount);
                    return !Number.isNaN(amount) && amount > 0;
                });
                
                if (allAmountsValid) {
                    this.setState({
                        credit_amount: String(total)
                    });
                }
            }
        }
    }
    getUserCredits = (userID) => {
        const { CREDITS } = this.props;
        console.log('====================================');
        console.log("Get user credit list");
        console.log('====================================');
        let creditList = Object.values(CREDITS).filter( 
            item => ((item.credit_user_id === userID ) && (item.credit_op === "CREDIT")  && (item.credit_available))
        )

        console.log('====================================');
        console.log({creditList});
        console.log('====================================');
        this.setState({
            creditList,
            credit_id: "",
            credit_ref: "",
            available: ""
        })
    }
    handleChange = (e) => {
        const { name, value} = e.target;
        
        this.setState(prevState => ({
            [name]: value,
            errors: {
                ...prevState.errors,
                [name]: ""
            }
        }))
        console.log('================= HANDLE CHANGE ===================');
        console.log('====================================');
        if( (name === "credit_user_id") && this.props.debit){
            this.getUserCredits(value)
        }
    }
    handleChangeRef = (e) => {
        const { CREDITS } = this.props;
        const { name, value } = e.target;
        let credit = CREDITS[value]

        this.setState({
            [name]: credit.credit_ref,
            credit_id: value,
            available: credit.credit_total_available
        })
        
    }
    handleChangeDate = (date) => {
        this.setState({
            credit_creation_date: date
        })
    }

    handleAddSubCredit = () => {
        this.setState(prevState => ({
            subCredits: [
                ...prevState.subCredits,
                {
                    s_credit_amount: "",
                    s_credit_desc: "",
                    s_credit_spending_demand: prevState.credit_spending_demand_id || ""
                }
            ],
            subCreditsErrors: [
                ...prevState.subCreditsErrors,
                {}
            ],
            errors: {
                ...prevState.errors,
                subCredits: ""
            }
        }));
    }

    handleRemoveSubCredit = (index) => {
        this.setState(prevState => {
            const subCredits = prevState.subCredits.filter((_, idx) => idx !== index);
            const subCreditsErrors = prevState.subCreditsErrors.filter((_, idx) => idx !== index);
            return {
                subCredits,
                subCreditsErrors,
                errors: {
                    ...prevState.errors,
                    subCredits: ""
                }
            };
        });
    }

    handleSubCreditChange = (index, field, value) => {
        this.setState(prevState => {
            const subCredits = prevState.subCredits.map((item, idx) =>
                idx === index ? { ...item, [field]: value } : item
            );
            const subCreditsErrors = prevState.subCreditsErrors.map((item, idx) =>
                idx === index ? { ...item, [field]: "" } : item
            );
            return {
                subCredits,
                subCreditsErrors,
                errors: {
                    ...prevState.errors,
                    subCredits: ""
                }
            };
        });
    }

    getSubCreditsTotal = () => {
        return this.state.subCredits.reduce((acc, item) => {
            const amount = parseFloat(item.s_credit_amount);
            if (!item.s_credit_amount || Number.isNaN(amount)) {
                return acc;
            }
            return acc + amount;
        }, 0);
    }

    setFiles = (credit_files, error = "") => {
        console.log("[INFO] SET_PHOTO", credit_files);
        this.setState({
            credit_files,
            errors:{
                ...this.state.errors,
                credit_files:error
            }
        })
    }
    validate = () => {
        const spendingDemandOptions = this.getSpendingDemandOptions();
        const hasSpendingDemands = spendingDemandOptions.length > 0;
        let required = [
            "credit_user_id",
            "credit_amount",
            "credit_ref",
            "credit_payment_mode",
            "credit_bank",
            "credit_files"
        ]
        let noError = true
        let errors = {}
        let subCreditsErrors = []

        required.forEach(field => {
            if( isEmpty(this.state[field]) ) { // isEmpty doesn't work on dates, !doesn't work for arrays
                console.log(`${field} is empty`)
                noError =  false
                errors[field] = "Veuillez remplir ce Champ" 
            } else {
                errors[field] = "" 
            }
        })

        errors.subCredits = ""

        if (!this.props.debit && this.state.subCredits.length > 0) {
            let subCreditsValid = true
            let total = 0

            subCreditsErrors = this.state.subCredits.map(subCredit => {
                const rowErrors = {}

                if (!subCredit.s_credit_amount) {
                    rowErrors.s_credit_amount = "Le montant est requis"
                    subCreditsValid = false
                } else {
                    const amount = parseFloat(subCredit.s_credit_amount)
                    if (Number.isNaN(amount) || amount <= 0) {
                        rowErrors.s_credit_amount = "Montant invalide"
                        subCreditsValid = false
                    } else {
                        total += amount
                    }
                }

                if (hasSpendingDemands && !subCredit.s_credit_spending_demand) {
                    rowErrors.s_credit_spending_demand = "Sélectionnez une demande";
                    subCreditsValid = false
                }

                return rowErrors
            })

            const creditAmount = parseFloat(this.state.credit_amount || 0)
            if (subCreditsValid && !Number.isNaN(creditAmount)) {
                const difference = Math.abs(creditAmount - total)
                if (difference > 0.01) {
                    errors.subCredits = "La somme des sous-crédits doit être égale au montant du crédit."
                    subCreditsValid = false
                }
            } else if (subCreditsValid && Number.isNaN(creditAmount)) {
                errors.subCredits = "Le montant du crédit est invalide."
                subCreditsValid = false
            }

            if (!subCreditsValid) {
                noError = false
            }
        }

        this.setState({
            errors,
            subCreditsErrors
        })
        
        return noError
    }
    handleSubmit = async () => {

        if( this.validate() ){
            try {
                // Extraire les sous-crédits avant de créer le crédit
                // Note: s_credit_user_id sera ajouté après la création du crédit avec l'ID du crédit parent
                const subCredits = !this.props.debit && this.state.subCredits.length > 0
                    ? this.state.subCredits.map(subCredit => ({
                        s_credit_user_id: this.state.credit_user_id, // Utiliser l'utilisateur du crédit parent
                        s_credit_amount: parseFloat(subCredit.s_credit_amount),
                        s_credit_desc: subCredit.s_credit_desc,
                        s_credit_spending_demand: subCredit.s_credit_spending_demand
                    }))
                    : []

                // Préparer les données du crédit (sans les sous-crédits)
                let data = {}
                data.credit_amount =  parseFloat(this.state.credit_amount)
                data.credit_payment_mode =  this.state.credit_payment_mode
                data.credit_user_id =  this.state.credit_user_id
                data.credit_bank =  this.state.credit_bank
                data.credit_ref =  this.state.credit_ref
                data.credit_files =  this.state.credit_files
                data.credit_op = this.props.debit ? "DEBIT" : "CREDIT"
                data.credit_creation_date =  this.state.credit_creation_date

                console.log('====================================');
                console.log("submit credit", {data});
                console.log('====================================');

                if( this.props.debit) {
                    await this.props.newDebit(data)
                } else {
                    // Créer le crédit d'abord
                    const createdCredit = await this.props.newCredit(data)
                    
                    // Vérifier s'il y a des erreurs
                    if(this.props.STATE.credits.error){
                        this.props.notif("error",`Un problème est survenu: ${this.props.STATE.credits.error}`)
                        return
                    }

                    // Si le crédit a été créé avec succès et qu'il y a des sous-crédits
                    if (createdCredit && subCredits.length > 0) {
                        // Récupérer l'ID du crédit (peut être credit_id ou id selon l'API)
                        const creditId = createdCredit.credit_id || createdCredit.id
                        
                        if (!creditId) {
                            console.error("[ERROR] Credit ID not found in response:", createdCredit)
                            this.props.notif("error", "Impossible de récupérer l'ID du crédit créé")
                            return
                        }

                        console.log('====================================');
                        console.log("Creating sub-credits for credit:", creditId);
                        console.log('====================================');

                        // Créer chaque sous-crédit
                        let successCount = 0
                        let errorCount = 0
                        for (const subCredit of subCredits) {
                            try {
                                await this.props.newSubCredit(creditId, subCredit)
                                successCount++
                            } catch (subCreditError) {
                                errorCount++
                                console.error("[ERROR] Failed to create sub-credit:", subCreditError)
                                // On continue quand même pour créer les autres sous-crédits
                            }
                        }

                        if (errorCount > 0) {
                            this.props.notif("warning", `${successCount} sous-crédit(s) créé(s), ${errorCount} erreur(s)`)
                        }
                    }
                }

                // Vérifier les erreurs finales
                if(this.props.STATE.credits.error){
                    this.props.notif("error",`Un problème est survenu: ${this.props.STATE.credits.error}`)
                } else { 
                    this.props.notif("success", "Enregistré")
                    this.props.handleClose()
                }
            } catch (error) {
                console.error("[ERROR] Error in handleSubmit:", error)
                this.props.notif("error", `Erreur lors de l'enregistrement: ${error.message || "Erreur inconnue"}`)
            }
        } else {
            this.props.notif("error", "Le formulaire contient des erreurs")
        }
    }
    
    render() {
        const { BANKS, USERS, credit_demand_name } = this.props;
        const { creditList } = this.state;
        const subCreditsTotal = this.getSubCreditsTotal();
        const creditAmountNumber = parseFloat(this.state.credit_amount);
        const formattedCreditAmount = Number.isNaN(creditAmountNumber) ? 0 : creditAmountNumber;
        const modalTitle = credit_demand_name 
            ? `Créditer un compte utilisateur - ${credit_demand_name}` 
            : "Créditer un compte utilisateur";
        const spendingDemandOptions = this.getSpendingDemandOptions();
        const hasSpendingDemands = spendingDemandOptions.length > 0;
        console.log('====================================');
        console.log(this.state);
        console.log('====================================');
        return (
            <Dialog
                close={this.props.handleClose}
                title={modalTitle}
                submitAction={this.handleSubmit}
                submitTitle="Créditer"
                maxWidth={this.props.debit ? "sm" : "lg"}
            >
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <DatePicker  
                            id="credit_creation_date" 
                            label="Date de Paiement" 
                            name="credit_creation_date" 
                            value={this.state.credit_creation_date} 
                            handleChange={this.handleChangeDate} 
                            error={this.state.errors.credit_creation_date}
                            required
                            noPadding
                        />      
                    </Grid>   
                    <Grid item xs={12} >
                        <FormControl variant="outlined"  error={!!this.state.errors.credit_user_id} style={{width: "100%"}}>
                            <InputLabel htmlFor="credit_user_id">Utilisateur</InputLabel>
                            <Select
                                native
                                onChange={this.handleChange}
                                error={!!this.state.errors.credit_user_id}
                                value={this.state.credit_user_id}
                                input={
                                    <OutlinedInput 
                                        labelWidth={60} 
                                        name="credit_user_id" 
                                        id="credit_user_id" 
                                    />
                                }
                            >
                                <option value="" />
                                { Object.values(USERS).map( user  => (
                                    <option value={String(user.user_id)} key={user.user_id}>
                                        {user.username} {user.lastname} -  {user.email}
                                    </option>
                                )) }
                            </Select>
                            {/* <FormHelperText>{user Account}</FormHelperText> */}
                            <FormHelperText>{this.state.errors.credit_user_id}</FormHelperText>
                        </FormControl>
                    </Grid>                
                    {this.props.debit &&
                        <Grid item xs={12}>
                            <FormControl required error={!!this.state.errors.credit_ref} variant="outlined" style={{ width:'100%'}}>
                                <InputLabel htmlFor="credit_ref">Référence</InputLabel>
                                <Select
                                    value={this.state.credit_id}
                                    onChange={this.handleChangeRef}
                                    input={<OutlinedInput labelWidth={80} name="credit_ref" id="credit_ref" />}
                                    MenuProps={{
                                        style: { zIndex: 13002 },
                                        sx: { zIndex: 13002 },
                                        PaperProps: {
                                            style: { zIndex: 13002 }
                                        }
                                    }}
                                >
                                    {!isEmpty(creditList) &&
                                        creditList.map( item => (
                                            <MenuItem value={item.credit_id}>
                                                {new Date(item.credit_creation_date).toLocaleDateString('fr-FR')} - {item.credit_ref} - {item.credit_amount} - {item.credit_bank && BANKS[item.credit_bank].bank_name}
                                            </MenuItem>

                                        ))
                                    }
                                </Select>
                                <FormHelperText >{this.state.errors.credit_ref}</FormHelperText>
                            </FormControl>
                        </Grid>
                    }
                    <Grid item xs={12}>
                        <FormControl variant="outlined" style={{ width:'100%'}}>
                            <TextField
                                onChange={this.handleChange}
                                required
                                id="credit_amount"
                                error={!!this.state.errors.credit_amount}
                                helperText={this.state.errors.credit_amount}
                                value={this.state.credit_amount}
                                name="credit_amount"
                                label={(this.props.debit ? "Montant à Debiter" : "Montant à Créditer")}
                                variant="outlined"  
                                type="number"          
                                style={{width:"100%"}}
                            />
                        </FormControl>
                        {this.state.available &&
                            <FormHelperText >Disponible {this.state.available}</FormHelperText>
                        }
                    </Grid>
                    {!this.props.debit &&
                        <Fragment>
                            <Grid item xs={12}>
                                <FormControl variant="outlined" style={{ width:'100%'}}>
                                    <TextField
                                        onChange={this.handleChange}
                                        required
                                        id="credit_ref"
                                        error={!!this.state.errors.credit_ref}
                                        helperText={this.state.errors.credit_ref}
                                        value={this.state.credit_ref}
                                        name="credit_ref"
                                        label="Référence"
                                        variant="outlined"  
                                        style={{width:"100%"}}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl required error={!!this.state.errors.credit_payment_mode} variant="outlined" style={{ width:'100%'}}>
                                    <InputLabel htmlFor="credit_payment_mode">Mode de Paiement</InputLabel>
                                    <Select
                                        value={this.state.credit_payment_mode}
                                        onChange={this.handleChange}
                                        input={<OutlinedInput labelWidth={170} name="credit_payment_mode" id="credit_payment_mode" />}
                                        MenuProps={{
                                            style: { zIndex: 13002 },
                                            sx: { zIndex: 13002 },
                                            PaperProps: {
                                                style: { zIndex: 13002 }
                                            }
                                        }}
                                    >
                                        <MenuItem value="ESPECES">ESPECES</MenuItem>
                                        <MenuItem value="CHEQUE">CHEQUE</MenuItem>
                                        <MenuItem value="PRELEVEMENT">PRELEVEMENT</MenuItem>
                                        <MenuItem value="VIREMENT">VIREMENT</MenuItem>
                                        <MenuItem value="GAB">GAB</MenuItem>
                                        <MenuItem value="MOBILE MONEY">MOBILE MONEY</MenuItem>
                                    </Select>
                                    <FormHelperText >{this.state.errors.credit_payment_mode}</FormHelperText>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} >
                                <FormControl variant="outlined"  error={!!this.state.errors.credit_bank} style={{width: "100%"}}>
                                    <InputLabel htmlFor="credit_bank">Banque</InputLabel>
                                    <Select
                                        native
                                        onChange={this.handleChange}
                                        error={!!this.state.errors.credit_bank}
                                        value={this.state.credit_bank}
                                        input={
                                            <OutlinedInput 
                                                labelWidth={60} 
                                                name="credit_bank" 
                                                id="credit_bank" 
                                            />
                                        }
                                    >
                                        <option value= "" />
                                        { Object.values(BANKS).map( bank  => (
                                            <option value={bank.bank_id} key={bank.bank_id}>
                                                {bank.bank_name}
                                            </option>
                                        )) }
                                    </Select>
                                <FormHelperText>{this.state.errors.credit_bank}</FormHelperText>
                            </FormControl>
                        </Grid>        
                            <Grid item xs={12}>
                                <FileUpload
                                    label="Ajouter une Photo"
                                    setFiles={this.setFiles} 
                                    files={this.state.credit_files || []} 
                                />
                                <FormControl error={!!this.state.errors.credit_files} >
                                    <br/>
                                    <FormHelperText >{this.state.errors.credit_files}</FormHelperText>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="subtitle1">
                                        Sous-avance
                                    </Typography>
                                    <Button variant="outlined" color="primary" onClick={this.handleAddSubCredit}>
                                        Ajouter une sous-avance
                                    </Button>
                                </Box>
                                {this.state.subCredits.length > 0 && (
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Montant</TableCell>
                                                <TableCell>Description</TableCell>
                                                <TableCell>Demande de dépense</TableCell>
                                                <TableCell align="center">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {this.state.subCredits.map((subCredit, index) => (
                                                <TableRow key={`sub-credit-${index}`}>
                                                    <TableCell width="20%">
                                                        <TextField
                                                            variant="outlined"
                                                            size="small"
                                                            type="number"
                                                            value={subCredit.s_credit_amount}
                                                            onChange={(event) => this.handleSubCreditChange(index, "s_credit_amount", event.target.value)}
                                                            error={!!(this.state.subCreditsErrors[index] && this.state.subCreditsErrors[index].s_credit_amount)}
                                                            helperText={
                                                                this.state.subCreditsErrors[index] && this.state.subCreditsErrors[index].s_credit_amount
                                                                    ? this.state.subCreditsErrors[index].s_credit_amount
                                                                    : ""
                                                            }
                                                            style={{ width: "100%" }}
                                                        />
                                                    </TableCell>
                                                    <TableCell width="35%">
                                                        <TextField
                                                            variant="outlined"
                                                            size="small"
                                                            value={subCredit.s_credit_desc}
                                                            onChange={(event) => this.handleSubCreditChange(index, "s_credit_desc", event.target.value)}
                                                            style={{ width: "100%" }}
                                                        />
                                                    </TableCell>
                                                    <TableCell width="35%">
                                                        <Autocomplete
                                                            options={spendingDemandOptions}
                                                            value={this.getSpendingDemandOptionById(subCredit.s_credit_spending_demand, spendingDemandOptions)}
                                                            onChange={(event, newValue) =>
                                                                this.handleSubCreditChange(
                                                                    index,
                                                                    "s_credit_spending_demand",
                                                                    newValue ? newValue.spending_demand_id : ""
                                                                )
                                                            }
                                                            getOptionLabel={this.getSpendingDemandDisplay}
                                                            isOptionEqualToValue={(option, value) =>
                                                                option && value ? option.spending_demand_id === value.spending_demand_id : option === value
                                                            }
                                                            filterOptions={this.filterSpendingDemandOptions}
                                                            noOptionsText="Aucune demande correspondante"
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Demande de dépense"
                                                                    variant="outlined"
                                                                    size="small"
                                                                    error={
                                                                        !!(
                                                                            this.state.subCreditsErrors[index] &&
                                                                            this.state.subCreditsErrors[index].s_credit_spending_demand
                                                                        )
                                                                    }
                                                                    helperText={
                                                                        this.state.subCreditsErrors[index] &&
                                                                        this.state.subCreditsErrors[index].s_credit_spending_demand
                                                                            ? this.state.subCreditsErrors[index].s_credit_spending_demand
                                                                            : ""
                                                                    }
                                                                />
                                                            )}
                                                            disabled={!hasSpendingDemands}
                                                            clearOnBlur={false}
                                                            handleHomeEndKeys
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center" width="10%">
                                                        <IconButton
                                                            aria-label="Supprimer"
                                                            onClick={() => this.handleRemoveSubCredit(index)}
                                                            size="small"
                                                        >
                                                            <DeleteOutline fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow>
                                                <TableCell colSpan={4}>
                                                    <Box display="flex" justifyContent="flex-end">
                                                        <Typography variant="body2">
                                                            Total sous-avances : {subCreditsTotal.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / {formattedCreditAmount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                )}
                                {this.state.errors.subCredits && (
                                    <FormHelperText error>{this.state.errors.subCredits}</FormHelperText>
                                )}
                            </Grid>                
                        </Fragment>
                    }
                                                                                                                                    
                </Grid>
            </Dialog>
        );
    }
}

export default CreditForm;